import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, FolderDown, Images, Film } from "lucide-react";
import { mediaDownloadStatus, mediaDownloadUrl, mediaZipUrl } from "@/lib/videoApi";
import { Button } from "@/components/ui/button";

// Hand the download to the browser (native progress, nothing buffered in page memory; works in iPhone Safari).
const startDownload = (url) => {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  a.setAttribute("download", "");
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// One original file (photo, video or registration papers). videoKey kept as the prop name for existing callers.
export function VideoDownloadButton({ horseId, videoKey, compact, testId = "media-download" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const [s] = await mediaDownloadStatus(horseId, videoKey);
      if (!s?.ready) { toast.error(s?.message || "This file can't be downloaded right now."); return; }
      if (!s.original) toast.message("Downloading Cloudflare Stream's full-resolution MP4 (Stream doesn't keep the original upload).");
      startDownload(mediaDownloadUrl(horseId, videoKey));
    } catch (e) { toast.error(e.message || "The original file is unavailable."); } finally { setBusy(false); }
  };
  if (compact) return (
    <button type="button" data-testid={testId} onClick={go} disabled={busy} aria-label="Download original" className="text-[11px] text-amber-800 hover:underline inline-flex items-center gap-0.5 disabled:opacity-50">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}Download
    </button>
  );
  return (
    <button type="button" data-testid={testId} aria-label="Download original" title="Download original" onClick={go} disabled={busy}
      className="h-11 w-11 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 flex items-center justify-center shrink-0 disabled:opacity-50">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
    </button>
  );
}

const CATS = { photos: ["photo"], videos: ["video"], all: ["photo", "video", "paper"] };
const LABEL = { photos: "photos", videos: "videos", all: "files" };

function ZipButton({ horseId, scope, icon: Icon, children }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const rows = (await mediaDownloadStatus(horseId)).filter((r) => CATS[scope].includes(r.category));
      const ready = rows.filter((r) => r.ready);
      if (!rows.length) { toast.error(`This horse has no ${LABEL[scope]} to download.`); return; }
      if (!ready.length) { toast.error(rows.map((r) => r.message).find(Boolean) || "Nothing is ready to download yet."); return; }
      const skipped = rows.length - ready.length;
      toast.message(`Preparing a ZIP of ${ready.length} ${LABEL[scope]}${skipped ? ` (${skipped} unavailable, listed in NOT_INCLUDED.txt)` : ""}. Your browser shows the download progress.`);
      startDownload(mediaZipUrl(horseId, scope));
    } catch (e) { toast.error(e.message || "Could not create the ZIP file."); } finally { setBusy(false); }
  };
  return (
    <Button data-testid={`download-all-${scope === "all" ? "media" : scope}`} variant="outline" onClick={go} disabled={busy} className="h-10 rounded-full border-stone-300 text-stone-700">
      {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Icon className="h-4 w-4 mr-2" />}{children}
    </Button>
  );
}

export function MediaDownloadBar({ horseId }) {
  return (
    <div data-testid="media-download-bar" className="flex flex-wrap gap-2 mb-3">
      <ZipButton horseId={horseId} scope="photos" icon={Images}>Download All Photos</ZipButton>
      <ZipButton horseId={horseId} scope="videos" icon={Film}>Download All Videos</ZipButton>
      <ZipButton horseId={horseId} scope="all" icon={FolderDown}>Download All Media</ZipButton>
    </div>
  );
}

// Key used by the download API for a horse media item.
export const mediaKey = (m) => (m.source === "stream" ? m.video_id : m.smartsheet_attachment_id);
